/**
 * Signature Extractor
 *
 * Best-effort extraction of the sender's email signature from the HTML body.
 * High-confidence markers/delimiters only — returns empty text when unsure.
 */

/* global Office */

const MAX_HTML_BYTES = 200 * 1024;
const MAX_DELIMITER_LINES = 40;
const MAX_DELIMITER_CHARS = 2048;

const SIGNATURE_SELECTORS = [
  '#Signature',
  '#_MailAutoSig',
  '#divSignature',
  '.gmail_signature',
  '[id$="Signature"]',
  '[id$="_MailAutoSig"]'
];

/**
 * Extracts plain-text signature from an Outlook email item.
 */
export class SignatureExtractor {
  /**
   * @param {Office.MessageRead} emailItem
   * @returns {Promise<{ text: string }>}
   */
  static async extract(emailItem) {
    try {
      const html = await this._getHtmlBody(emailItem);
      if (!html) {
        return { text: '' };
      }

      const safeHtml = this._prepareHtml(html);
      const doc = new DOMParser().parseFromString(safeHtml, 'text/html');
      const latest = this._isolateLatestMessage(doc);
      const text = this._findSignature(latest);
      return { text };
    } catch (error) {
      console.error('Signature extraction failed:', error);
      return { text: '' };
    }
  }

  static _getHtmlBody(emailItem) {
    return new Promise((resolve, reject) => {
      if (!emailItem?.body?.getAsync) {
        resolve('');
        return;
      }

      emailItem.body.getAsync(Office.CoercionType.Html, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value || '');
        } else {
          reject(new Error(result.error?.message || 'Failed to get email body'));
        }
      });
    });
  }

  /**
   * Strip scripts/styles/comments and cap size before DOMParser.
   */
  static _prepareHtml(html) {
    let cleaned = String(html)
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<!--[\s\S]*?-->/g, '');

    if (cleaned.length > MAX_HTML_BYTES) {
      cleaned = cleaned.slice(0, MAX_HTML_BYTES);
    }

    return cleaned;
  }

  /**
   * Remove quoted reply/forward history from the document body.
   */
  static _isolateLatestMessage(doc) {
    const body = doc.body;
    if (!body) {
      return doc.createElement('div');
    }

    const quoteSelectors = [
      '#divRplyFwdMsg',
      '.OutlookMessageHeader',
      'blockquote[type="cite"]',
      'div.gmail_quote',
      '#appendonsend'
    ];

    for (const selector of quoteSelectors) {
      const quoteEl = body.querySelector(selector);
      if (quoteEl) {
        let node = quoteEl;
        while (node) {
          const next = node.nextSibling;
          node.parentNode?.removeChild(node);
          node = next;
        }
        break;
      }
    }

    return body;
  }

  /**
   * High-confidence signature detection only.
   */
  static _findSignature(root) {
    if (!root) {
      return '';
    }

    for (const selector of SIGNATURE_SELECTORS) {
      const el = root.querySelector(selector);
      if (el) {
        const text = this._normalizeText(el);
        if (text) {
          return text;
        }
      }
    }

    return this._findDelimiterSignature(root);
  }

  /**
   * RFC 3676: line that is exactly "--" or "-- " starts the signature.
   */
  static _findDelimiterSignature(root) {
    const plain = this._normalizeText(root);
    if (!plain) {
      return '';
    }

    const lines = plain.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line === '--' || line === '-- ') {
        const after = lines.slice(i + 1);
        if (after.length === 0 || after.length > MAX_DELIMITER_LINES) {
          return '';
        }
        const text = after.join('\n').trim();
        if (!text || text.length > MAX_DELIMITER_CHARS) {
          return '';
        }
        return text;
      }
    }

    return '';
  }

  static _normalizeText(node) {
    if (!node) {
      return '';
    }

    const clone = node.cloneNode(true);
    const ownerDoc = clone.ownerDocument || document;

    const blockTags = clone.querySelectorAll(
      'br, p, div, tr, li, h1, h2, h3, h4, h5, h6, hr'
    );
    blockTags.forEach((el) => {
      if (el.tagName === 'BR' || el.tagName === 'HR') {
        el.replaceWith(ownerDoc.createTextNode('\n'));
      } else {
        el.appendChild(ownerDoc.createTextNode('\n'));
      }
    });

    return String(clone.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }
}
